import { useEffect, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import client from '@/api/client';

export function VerifyEmailPage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [resendEmail, setResendEmail] = useState('');
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    client.get(`/api/auth/verify-email?token=${token}`)
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
  }, [token]);

  async function handleResend() {
    if (!resendEmail.trim()) return;
    setResendState('sending');
    try {
      await client.post('/api/auth/resend-verification', { identifier: resendEmail.trim() });
      setResendState('sent');
    } catch {
      setResendState('error');
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm text-center">
        {status === 'loading' && (
          <>
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600 text-sm">Verifying your email…</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Email Verified!</h2>
            <p className="text-sm text-gray-500 mb-6">Your email has been verified. You can now log in.</p>
            <Link to="/login" className="block w-full py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition">
              Go to Login
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Verification Failed</h2>
            <p className="text-sm text-gray-500 mb-5">The link may be invalid or expired. Enter your email to get a new one.</p>

            {resendState === 'sent' ? (
              <p className="text-sm text-green-600 font-medium mb-4">New verification link sent — check your inbox.</p>
            ) : (
              <div className="text-left mb-4 space-y-2">
                <input
                  type="email"
                  placeholder="Your email address"
                  value={resendEmail}
                  onChange={e => setResendEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') void handleResend(); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {resendState === 'error' && (
                  <p className="text-xs text-red-500">Something went wrong. Please try again.</p>
                )}
                <button
                  onClick={() => void handleResend()}
                  disabled={resendState === 'sending' || !resendEmail.trim()}
                  className="w-full py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 transition"
                >
                  {resendState === 'sending' ? 'Sending…' : 'Resend Verification Email'}
                </button>
              </div>
            )}

            <Link to="/login" className="block w-full py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition">
              Back to Login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
